package com.iregained.site.components.sections.header

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A

// UNIQUE ANIMATIONS: Curtain Drop for the main header, Subtle Dropdown Fade for menus
val HeaderCurtainDropKeyframes = Keyframes {
    0.percent { Modifier.translateY((-100).percent).opacity(0) }
    100.percent { Modifier.translateY(0.percent).opacity(1) }
}

@Composable
fun NavHeader(data: NavHeaderData = defaultNavHeaderData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    var isMobileMenuOpen by remember { mutableStateOf(false) }

    val ctx = rememberPageContext()
    val currentPath = ctx.route.path.removeSuffix("/").ifEmpty { "/" }

    Column(
        modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White)
            .boxShadow(0.px, 4.px, 20.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.05f))
            .position(Position.Sticky).top(0.px).zIndex(999).animation(
                HeaderCurtainDropKeyframes.toAnimation(
                    duration = 0.8.s, timingFunction = AnimationTimingFunction.EaseOut
                )
            )
    ) {
        Box(
            modifier = Modifier.fillMaxWidth().padding(leftRight = 5.percent, topBottom = 15.px),
            contentAlignment = Alignment.Center
        ) {
            // Layer 1: The Centered Navigation Links
            if (isDesktop) {
                Row(
                    modifier = Modifier.gap(12.px), verticalAlignment = Alignment.CenterVertically
                ) {
                    data.menuItems.forEach { item ->
                        val isLinkActive = item.path.removeSuffix("/").ifEmpty { "/" } == currentPath
                        HeaderLink(text = item.title, path = item.path, isActive = isLinkActive)
                    }
                }
            }

            // Layer 2: The Edges (Logo Left, Contact Right)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Link(path = "/") {
                    Image(
                        src = data.logoPath,
                        modifier = LogoStyle.toModifier().unextractable().height(55.px).cursor(Cursor.Pointer)
                    )
                }

                if (isDesktop) {
                    Row(
                        modifier = Modifier.gap(12.px), verticalAlignment = Alignment.CenterVertically
                    ) {
                        DynamicUtilityLink(
                            data.utilityPhoneIcon,
                            data.contactPhone,
                            "tel:${data.contactPhone.replace(Regex("[^0-9+]"), "")}"
                        )
                        DynamicUtilityLink(data.utilityEmailIcon, data.contactEmail, "mailto:${data.contactEmail}")
                    }
                } else {
                    Box(
                        modifier = HamburgerIconStyle.toModifier().size(30.px).cursor(Cursor.Pointer)
                            .onClick { isMobileMenuOpen = !isMobileMenuOpen }, contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            text = if (isMobileMenuOpen) "✕" else "☰",
                            modifier = Modifier.fontSize(24.px).color(palette.brand.primaryBlue)
                        )
                    }
                }
            }
        }

        // --- MOBILE MENU DROPDOWN ---
        if (!isDesktop && isMobileMenuOpen) {
            Column(
                modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White)
                    .padding(leftRight = 5.percent, topBottom = 20.px)
                    .borderTop(1.px, LineStyle.Solid, palette.custom.divider)
                    .borderBottom(3.px, LineStyle.Solid, palette.brand.primaryBlue)
            ) {
                data.menuItems.forEach { item ->
                    val isLinkActive = item.path.removeSuffix("/").ifEmpty { "/" } == currentPath
                    MobileHeaderLink(
                        text = item.title,
                        path = item.path,
                        isActive = isLinkActive,
                        onClick = { isMobileMenuOpen = false })
                }

                Column (
                    modifier = Modifier.fillMaxWidth().padding(top = 8.px, bottom = 8.px).gap(6.px).borderTop(1.px, LineStyle.Solid, palette.custom.divider),
                    verticalArrangement = Arrangement.Center
                ) {
                    DynamicUtilityLink(
                        data.utilityPhoneIcon,
                        data.contactPhone,
                        "tel:${data.contactPhone.replace(Regex("[^0-9+]"), "")}"
                    )
                    DynamicUtilityLink(data.utilityEmailIcon, data.contactEmail, "mailto:${data.contactEmail}")
                }
            }
        }
    }
}

@Composable
private fun HeaderLink(text: String, path: String, isActive: Boolean) {
    val palette = ColorMode.current.toSitePalette()

    Link(
        path = path,
        text = text,
        modifier = AnimatedHeaderLinkStyle.toModifier().fontSize(20.px)
            .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
            .textDecorationLine(TextDecorationLine.None).whiteSpace(WhiteSpace.NoWrap).then(
                // Force the active state styles if this is the current page
                if (isActive) Modifier.backgroundColor(palette.brand.primarySoft).color(palette.brand.primaryBlue)
                else Modifier
            )
    )
}

@Composable
private fun MobileHeaderLink(text: String, path: String, isActive: Boolean = false, onClick: () -> Unit = {}) {
    val palette = ColorMode.current.toSitePalette()
    Link(
        path = path,
        text = text,
        modifier = Modifier.fillMaxWidth().padding(topBottom = 14.px, leftRight = 16.px).borderRadius(8.px)
            .backgroundColor(if (isActive) palette.brand.primarySoft else Colors.Transparent)
            .color(if (isActive) palette.brand.primaryBlue else palette.custom.darkText)
            .fontWeight(if (isActive) FontWeight.ExtraBold else FontWeight.Bold).fontSize(18.px)
            .textDecorationLine(TextDecorationLine.None).onClick { onClick() })
}

@Composable
private fun DynamicUtilityLink(iconPath: String, text: String, href: String) {
    val palette = ColorMode.current.toSitePalette()

    A(
        href = href, attrs = UtilityLinkStyle.toModifier().textDecorationLine(TextDecorationLine.None).toAttrs()
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(8.px)
        ) {
            Image(iconPath, Modifier.unextractable().size(16.px))
            SpanText(
                text = text,
                modifier = Modifier.fontSize(16.px).color(palette.custom.darkText).fontWeight(FontWeight.SemiBold)
                    .whiteSpace(WhiteSpace.NoWrap)
            )
        }
    }
}

// ==========================================
// DATA MODELS & CMS IMAGE SPECS
// ==========================================
data class NavLinkData(val text: String, val path: String)

/**
 * A Menu Item can either be a direct link (if path is provided)
 * OR a dropdown (if subItems list is populated).
 */
data class NavMenuItemData(
    val title: String, val path: String, val subItems: List<NavLinkData> = emptyList()
)

/**
 * CMS IMAGE UPLOAD SPECS FOR HEADER:
 * @field logoPath:
 * - Aspect Ratio: Flexible (Horizontal/Landscape).
 * - Recommended Dimensions: Max height 80px, Max width 300px.
 * - Format: SVG (Ideal) or transparent PNG.
 * * @field utilityPhoneIcon / utilityEmailIcon:
 * - Aspect Ratio: 1:1 (Perfect Square).
 * - Recommended Dimensions: 24x24px.
 * - Format: SVG Only. Should be single-color (dark gray) to match text.
 */
data class NavHeaderData(
    val logoPath: String,
    val utilityPhoneIcon: String,
    val contactPhone: String,
    val utilityEmailIcon: String,
    val contactEmail: String,
    val utilityExtraLinkText: String,
    val utilityExtraLinkPath: String,
    val ctaPath: String,
    val menuItems: List<NavMenuItemData>
)

val defaultNavHeaderData = NavHeaderData(
    logoPath = Res.Image.BRAND_LOGO,
    utilityPhoneIcon = Res.Icons.CALL,
    contactPhone = "0281-2364168",
    utilityEmailIcon = Res.Icons.SEND_EMAIL,
    contactEmail = "jexonplastech@gmail.com",
    utilityExtraLinkText = "Contact Us",
    utilityExtraLinkPath = "/contact",
    ctaPath = "/contact",
    menuItems = listOf(
        NavMenuItemData(title = "About Us", path = "/about"),
        NavMenuItemData(title = "Products", path = "/products"),
        NavMenuItemData(title = "Contact Us", path = "/contact"),
    )
)